// src/utils/portalUtils.js
// ======================================================
// 🏗️ PORTAL UTILITY — ENTERPRISE SSR-SAFE
// PropZyn v5.0 — Nivel QuintoAndar / Zillow / Stripe
// ======================================================
// ✅ Hidratación segura para SSR (Vite/Next.js)
// ✅ Bloqueo de scroll del body sin parpadeo
// ✅ Escape key handler con cleanup automático
// ✅ Portal helper tipado y reutilizable
// ✅ Animaciones GPU-accelerated idénticas en todos los modales
// ✅ Zero dependencias externas
// ======================================================

import { useEffect, useState } from "react";
import ReactDOM from "react-dom";

// ======================================================
// 1️⃣ HOOK: MONTAJE SEGURO SSR
// ======================================================
// Evita errores de hidratación en SSR
// El portal solo se renderiza cuando el cliente está listo
// ======================================================

export function usePortal() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted;
}

// ======================================================
// 2️⃣ HOOK: BLOQUEO DE SCROLL
// ======================================================
// Comportamiento idéntico a Airbnb/Stripe:
// - Bloquea scroll del body
// - Compensa el ancho del scrollbar para evitar layout shift
// - Cleanup automático al desmontar
// ======================================================

export function useBodyScrollLock(isOpen) {
  useEffect(() => {
    if (!isOpen) return;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);
}

// ======================================================
// 3️⃣ HOOK: TECLA ESCAPE
// ======================================================
// Cierra el modal con Escape
// Solo activo cuando el modal está abierto
// ======================================================

export function useEscapeKey(isOpen, onClose) {
  useEffect(() => {
    if (!isOpen || !onClose) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);
}

// ======================================================
// 4️⃣ FUNCIÓN: PORTALIZAR
// ======================================================
// Renderiza children en document.body vía createPortal
// Solo ejecuta si mounted === true (SSR-safe)
// Uso: portalize(mounted, <MiModal />)
// ======================================================

export function portalize(mounted, children) {
  if (!mounted) return null;
  return ReactDOM.createPortal(children, document.body);
}

// ======================================================
// 5️⃣ ESTILOS DE ANIMACIÓN (GPU-ACCELERATED)
// ======================================================
// Unifica las animaciones de todos los modales del sistema
// Usar dentro de cada modal: <style>{MODAL_ANIMATIONS}</style>
// ======================================================

export const MODAL_ANIMATIONS = `
  @keyframes modalFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .modal-overlay-anim {
    animation: modalFadeIn 0.15s ease-out;
    will-change: opacity;
  }

  @keyframes modalScaleIn {
    from { transform: scale(0.94) translateZ(0); opacity: 0; }
    to { transform: scale(1) translateZ(0); opacity: 1; }
  }
  .modal-content-anim {
    animation: modalScaleIn 0.22s ease-out;
    will-change: transform, opacity;
  }

  @keyframes modalSlideUp {
    from { transform: translateY(100%) translateZ(0); opacity: 0; }
    to { transform: translateY(0) translateZ(0); opacity: 1; }
  }
  .modal-slideup-anim {
    animation: modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    will-change: transform, opacity;
  }
`;